import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';

import { getRequestObserver } from '@/lib/axios';
import {
  MANAGEMENT_PAGE_SIZE as pageSize,
  REQUEST_STALE_TIME_IN_MS,
  REWARD_TRIGGER_EVENT,
  REWARDS_TRIGGER_EVENTS,
} from '@/constants';
import { isStudent } from '@/functions/auth';
import { Course, CourseFormatEnum } from '@/models/Course';
import Enrollment, { EnrollmentStatusEnum } from '@/models/Enrollment';
import Klass from '@/models/Klass';
import LessonProgressDashboardStats from '@/models/LessonProgressDashboardStats';
import Rewards from '@/models/Rewards';
import ScheduledLesson from '@/models/ScheduledLesson';
import { SimplifiedCourseProgress } from '@/models/StudentCourseProgress';
import StudentDashboardStats from '@/models/StudentDashboard';
import { enrollmentValidation } from '@/utils/enrollmentValidation';
import Observable from '@/utils/observers/ObserverPattern';
import { RequestEvent } from '@/utils/observers/RequestObserver';
import { useInfiniteCache } from '@/data/hook/query/v2/useCacheManager';
import useAuth from '@/data/hook/useAuth';
import useListService from '@/data/hook/useListService';
import { ListCourseProgressFilters } from '@/data/services/courseProgressServices';
import { listDashboardLessonProgress } from '@/data/services/dashboardLessonProgress';
import {
  dashboardLessonProgressQueryKeys,
  enrollmentsQueryKeys,
  klassesQueryKeys,
  klassLeaderBoardQueryKeys,
  nextStepsQueryKeys,
  rewardsQueryKeys,
} from '@/data/services/querykeys';
import { scheduledLessonQueries } from '@/data/query-keys/scheduledLessonQueryKey';
import { studentDashboardQueries } from '@/data/query-keys/studentDashboardQueryKey';
import { listStudentDashboardStats } from '@/data/services/studentDashboardStatsServices';

type StudentContextParams = {
  slugCourseName?: string;
};

/**
 * @deprecated Legacy student god-context kept only for the old UI during the
 * new UI migration. For the new student dashboard, use the page-scoped data
 * layer instead: `useStudentDashboard` from
 * `@/data/context/StudentDashboardContext` and the domain
 * hooks in `@/data/hook/student-dashboard/`. Will be removed once the
 * migration is complete.
 */
export interface StudentContextProps {
  updateProgress(): Promise<void>;
  selectProgress(courseProgressId: number): void;

  rewardsData?: Rewards;
  isLoadingRewards: boolean;
  shouldAnimateReward: boolean;

  nextSteps: Course[];

  loadingNextLesson?: boolean;
  nextLessonError?: unknown;
  nextLesson?: ScheduledLesson;

  loadingKlass: boolean;
  klassError: unknown;
  klass?: Klass;

  progress: SimplifiedCourseProgress[];
  currentProgress?: SimplifiedCourseProgress;

  setParams: (params: StudentContextParams) => void;

  validEnrollment: boolean;

  dashboardStatsError?: unknown;
  dashboardStats?: StudentDashboardStats;
  dashboardStatsCollection: StudentDashboardStats[];
  loadingDashboard?: boolean;

  updateProgressObserver: Observable<number, SimplifiedCourseProgress>;

  updateLessonProgressDashboardStats: (
    lessonProgressId: number,
  ) => Promise<void>;

  courseProgressFilters?: ListCourseProgressFilters;

  enrollments: Enrollment[];

  extraCoursesStats: StudentDashboardStats[];

  otherCoursesStats: StudentDashboardStats[];
}

/**
 * @deprecated See {@link StudentContextProps}.
 */
export const StudentContext = createContext<StudentContextProps>(
  {} as StudentContextProps,
);

/**
 * @deprecated See {@link StudentContextProps}.
 */
export function StudentProvider({ children }: PropsWithChildren) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const enabled = !!user && isStudent(user.userType);

  const [shouldAnimateReward, setShouldAnimateReward] =
    useState<boolean>(false);

  const previousRewardsData = useRef<Rewards>();

  const [{ slugCourseName = '' }, setParams] = useState<StudentContextParams>(
    {},
  );

  const [currentProgress, setCurrentProgress] =
    useState<SimplifiedCourseProgress>();

  const [dashboardStats, setDashboardStats] = useState<StudentDashboardStats>();

  const { queryFn: rewardsQueryFn, queryKey: rewardsQueryKey } =
    rewardsQueryKeys.get(user?.id ?? 0);

  const { data: rewardsData, isInitialLoading: isLoadingRewards } = useQuery({
    queryKey: rewardsQueryKey,
    queryFn: rewardsQueryFn,
    enabled,
  });

  const eventMatches = (
    currentEvent: RequestEvent,
    expectedEvent: REWARD_TRIGGER_EVENT,
  ) => {
    const isExpectedUrl = expectedEvent.url.test(currentEvent.url);
    const isExpectedMethod = currentEvent.method === expectedEvent.method;
    const isExpectedStatus =
      currentEvent.statusCode === expectedEvent.statusCode;

    return isExpectedUrl && isExpectedMethod && isExpectedStatus;
  };

  useEffect(() => {
    const observerId = 'rewards';
    const requestObserver = getRequestObserver();

    if (!enabled) return;

    const onTriggerRewards = async (event: RequestEvent) => {
      for (const expectedEvent of REWARDS_TRIGGER_EVENTS) {
        if (eventMatches(event, expectedEvent)) {
          previousRewardsData.current = rewardsData;
          await queryClient.invalidateQueries({
            queryKey: rewardsQueryKey,
          });
          await queryClient.invalidateQueries({
            queryKey: klassLeaderBoardQueryKeys.list._def,
          });
          break;
        }
      }
    };

    requestObserver.subscribe({
      id: observerId,
      action: onTriggerRewards,
    });

    return () => {
      requestObserver.unsubscribe(observerId);
    };
  }, [queryClient, rewardsQueryKey, enabled, rewardsData]);

  useEffect(() => {
    let mounted = true;

    if (!mounted || !rewardsData) return;

    const rewardsHasChange =
      previousRewardsData.current?.coins !== rewardsData.coins;

    if (!previousRewardsData.current || !rewardsHasChange) return;

    setShouldAnimateReward(true);
    const timeOut = setTimeout(() => {
      setShouldAnimateReward(false);
    }, 2000);

    previousRewardsData.current = rewardsData;

    return () => {
      mounted = false;
      clearTimeout(timeOut);
    };
  }, [rewardsData]);

  const { results: enrollments, isInitialLoading: isLoadingEnrollments } =
    useListService({
      ...enrollmentsQueryKeys.byStudent(user?.id || 0, {
        status: [
          EnrollmentStatusEnum.ACTIVE,
          EnrollmentStatusEnum.CONCLUDED,
          EnrollmentStatusEnum.CANCELED,
        ],
      }),
      enabled,
    });

  const courseProgressFilters = {
    studentId: user?.id ?? 0,
    isActive: true,
    startSoon: enrollments.length > 1 ? false : undefined,
  };

  const {
    results: dashboardStatsCollection,
    isInitialLoading: loadingDashboardStats,
    error: dashboardStatsError,
    cacheManager: dashboardStatsCacheManager,
  } = useListService({
    ...studentDashboardQueries.courseProgresses(courseProgressFilters),
    enabled,
    staleTime: REQUEST_STALE_TIME_IN_MS,
  });

  const loadingDashboard = loadingDashboardStats || isLoadingEnrollments;

  const { pathname } = useLocation();

  const hydrateDashboardWithSlug = useCallback(
    (dashboardStats: StudentDashboardStats[], slugCourseName: string) => {
      const dashboard = dashboardStats.find(
        stats => stats.coursePath.slug === slugCourseName,
      );

      if (!dashboard) return;

      setDashboardStats(dashboard);

      setCurrentProgress({ ...dashboard });
    },
    [],
  );

  const hydrateDashboard = useCallback(
    (dashboardStats: StudentDashboardStats[], slugCourseName?: string) => {
      if (slugCourseName) {
        return hydrateDashboardWithSlug(dashboardStats, slugCourseName);
      }

      const lastDashboardStats = dashboardStats.at(-1);

      if (!lastDashboardStats) return;

      setDashboardStats(lastDashboardStats);
      setCurrentProgress({ ...lastDashboardStats });
    },
    [hydrateDashboardWithSlug],
  );

  const validEnrollment = enrollmentValidation(
    currentProgress?.enrollmentStatus,
  );

  const enabledKlass = enabled && !!currentProgress && validEnrollment;

  const {
    data: klass,
    isInitialLoading: loadingKlass,
    error: klassError,
  } = useQuery({
    ...klassesQueryKeys.get(currentProgress?.klassId ?? 0),
    enabled: enabledKlass,
    staleTime: REQUEST_STALE_TIME_IN_MS,
  });

  const shouldUseKlassId = validEnrollment && !!klass?.id;

  const lessonProgressCacheManager =
    useInfiniteCache<LessonProgressDashboardStats>(
      dashboardLessonProgressQueryKeys.list({
        scheduledLessonActive: isStudent(user?.userType) || undefined,
        courseProgressId: [currentProgress?.id ?? 0],
        orderBy: shouldUseKlassId ? 'scheduled_lesson_order' : 'lesson_order',
        pageSize,
      })._ctx.infinity.queryKey,
    );

  const updateLessonProgressDashboardStats = useCallback(
    async (lessonProgressId: number) => {
      const updatedCollecion = await listDashboardLessonProgress({
        lessonProgressId: [lessonProgressId],
      });

      const updated = updatedCollecion.results.at(0);

      if (!updated) return;

      lessonProgressCacheManager.update(updated);
    },
    [lessonProgressCacheManager],
  );

  useEffect(() => {
    if (!dashboardStatsCollection.length) return;
    if (dashboardStatsCollection.length && dashboardStats && !slugCourseName)
      return;
    if (dashboardStats && dashboardStats.coursePath.slug === slugCourseName)
      return;

    hydrateDashboard(dashboardStatsCollection, slugCourseName);
  }, [
    dashboardStats,
    dashboardStatsCollection,
    slugCourseName,
    hydrateDashboard,
  ]);

  const extraCoursesStats = useMemo(() => {
    if (!dashboardStatsCollection) return [];
    return dashboardStatsCollection.filter(p =>
      p.coursePath.course.formats?.includes(CourseFormatEnum.EXTRA_COURSE),
    );
  }, [dashboardStatsCollection]);

  const otherCoursesStats = useMemo(() => {
    if (!dashboardStatsCollection) return [];
    return dashboardStatsCollection.filter(
      p =>
        !p.coursePath.course.formats?.includes(CourseFormatEnum.EXTRA_COURSE),
    );
  }, [dashboardStatsCollection]);

  const progress: SimplifiedCourseProgress[] = useMemo(() => {
    return dashboardStatsCollection.map(stats => ({
      ...stats,
    }));
  }, [dashboardStatsCollection]);

  const updateProgressObserver = useMemo(
    () => new Observable<number, SimplifiedCourseProgress>(),
    [],
  );

  const updateProgress = async () => {
    if (!dashboardStats || !user) return;

    const updatedCollecion = await listStudentDashboardStats({
      studentId: user.id,
      coursePathId: dashboardStats.coursePath.id,
    });

    const updated = updatedCollecion.results.at(0);
    if (!updated) return;

    setDashboardStats(updated);

    setCurrentProgress({ ...updated });

    updateProgressObserver.notifyAll(undefined, updated);

    dashboardStatsCacheManager.update(updated);
  };

  const selectProgress = useCallback(
    (courseProgressId: number) => {
      const find = progress.find(progress => progress.id === courseProgressId);

      if (!find) return;

      setCurrentProgress(find);

      const findDashboardStats = dashboardStatsCollection.find(
        stats => stats.id === find.id,
      );

      if (!findDashboardStats) return;

      setDashboardStats(findDashboardStats);
    },
    [progress, dashboardStatsCollection],
  );

  const {
    data: nextLesson,
    isInitialLoading: loadingNextLesson,
    error: nextLessonError,
  } = useQuery({
    enabled: enabled && !!klass,
    ...scheduledLessonQueries.next(klass?.id ?? 0),
    staleTime: REQUEST_STALE_TIME_IN_MS,
    retry: false,
  });

  const { results: nextSteps } = useListService({
    enabled: enabled && !!klass,
    staleTime: REQUEST_STALE_TIME_IN_MS,
    ...nextStepsQueryKeys.list(currentProgress?.id ?? 0),
  });

  const isLogin = pathname === '/login';

  useEffect(() => {
    if (isLogin) {
      setCurrentProgress(undefined);
      setDashboardStats(undefined);
      previousRewardsData.current = undefined;
    }
  }, [isLogin]);

  return (
    <StudentContext.Provider
      value={{
        progress,
        currentProgress,
        extraCoursesStats,

        loadingKlass,
        klass,
        klassError,

        rewardsData,
        isLoadingRewards,
        shouldAnimateReward,
        nextSteps,

        loadingNextLesson,
        nextLessonError,
        nextLesson,

        updateProgress,
        selectProgress,
        setParams,

        validEnrollment,

        loadingDashboard,
        dashboardStatsError,
        dashboardStats,
        dashboardStatsCollection,

        updateLessonProgressDashboardStats,

        courseProgressFilters,

        enrollments,
        otherCoursesStats,
        updateProgressObserver,
      }}
    >
      {children}
    </StudentContext.Provider>
  );
}

export default StudentContext;
